import React, { useState, useEffect } from 'react';
import { Button, Nav, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
    DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import axios from 'axios';
import { Buffer } from 'buffer';
import { BiLayout, BiArrowBack, BiShow, BiSave, BiBarChartAlt2, BiDownload, BiUpload } from 'react-icons/bi';
import { downloadTranslationExcel, parseTranslationExcel } from '../utils/formTranslationExcel';

import Topbar from '../component/Topbar';
import LoadingScreen from '../component/LoadingScreen';
import FormPreviewModal from '../component/FormPreviewModal';
import FieldPalette from './formBuilder/FieldPalette';
import BuilderCanvas from './formBuilder/BuilderCanvas';
import FieldEditor from './formBuilder/FieldEditor';
import FormSettings from './formBuilder/FormSettings';
import FormRespondents from './formBuilder/FormRespondents';
import FormReportPanel from './formBuilder/FormReportPanel';

import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import {
    AccessMode, TemplateField, TemplateSchema, TemplateDto, TemplateStatus,
    CHOICE_TYPES, DEFAULT_FIELD_LABELS, FieldType,
} from '../types/template';

type Tab = 'builder' | 'settings' | 'respondents' | 'reports';

function makeField(type: FieldType, order: number): TemplateField {
    return {
        id:         `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type,
        label:      DEFAULT_FIELD_LABELS[type],
        required:   false,
        order,
        options:    CHOICE_TYPES.includes(type)
            ? [{ id: `o_${Date.now()}`, label: 'Option 1' }]
            : undefined,
        maxStars:   type === 'star_rating' ? 5 : undefined,
        inputType:  type === 'textbox' ? 'text' : undefined,
        placeholder: '',
    };
}

export default function FormBuilder() {
    const { instance, accounts, inProgress } = useMsal();
    const navigate        = useNavigate();
    const [params]        = useSearchParams();
    const codeParam       = params.get('code') ?? '';

    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [role, setRole]           = useState<UserRole>('owner');
    const [token, setToken]         = useState('');
    const [photoData, setPhotoData] = useState<string | null>(null);

    // Form state
    const [templateCode, setTemplateCode] = useState('');
    const [name, setName]                 = useState('');
    const [description, setDescription]   = useState('');
    const [status, setStatus]             = useState<TemplateStatus>('Draft');
    const [openDate, setOpenDate]         = useState('');
    const [closeDate, setCloseDate]       = useState('');
    const [allowEdit, setAllowEdit]       = useState(false);
    const [notifyPublish, setNotifyPublish] = useState(false);
    const [notifySubmit, setNotifySubmit]   = useState(false);
    const [accessMode, setAccessMode]       = useState<AccessMode>('InvitedOnly');

    // Builder state
    const [fields, setFields]               = useState<TemplateField[]>([]);
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const [activeTab, setActiveTab]         = useState<Tab>('builder');
    const [showPreview, setShowPreview]     = useState(false);
    const [dragActiveType, setDragActiveType] = useState<FieldType | null>(null);

    // Multi-language state
    const [languages, setLanguages]       = useState<string[]>(['en']);
    const [names, setNames]               = useState<Record<string, string>>({});
    const [descriptions, setDescriptions] = useState<Record<string, string>>({});

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const selectedField = fields.find(f => f.id === selectedFieldId) ?? null;
    const api = process.env.REACT_APP_API_END_POINT;
    const auth = () => ({ headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` } });

    // ── Auth init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            try {
                const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r.data, 'binary').toString('base64'));
            } catch { /* no photo */ }
            const userRole = await checkRole(tokens.accessTokenProfile);
            saveRealRole(userRole);
            if (userRole !== 'admin' && userRole !== 'superadmin') {
                navigate('/accessdenied'); return;
            }
            setRole(getEffectiveRole(userRole));
            setToken(tokens.accessToken);
        });
    }, [inProgress, instance, accounts, navigate]);

    // ── Load form (edit mode) ─────────────────────────────────────────────
    useEffect(() => {
        if (!token) return;
        if (!codeParam) { setLoading(false); return; }

        axios.get<TemplateDto>(`${api}/v1/Form/detail`, { headers: { Authorization: `Bearer ${token}` }, params: { code: codeParam } })
            .then(res => {
                const t = res.data;
                setTemplateCode(t.templateCode);
                setName(t.name);
                setDescription(t.description ?? '');
                setStatus(t.status as TemplateStatus);
                setOpenDate(t.openDate  ? t.openDate.slice(0, 16)  : '');
                setCloseDate(t.closeDate ? t.closeDate.slice(0, 16) : '');
                setAllowEdit(t.allowEditResponse);
                setNotifyPublish(t.notifyOnPublish);
                setNotifySubmit(t.notifyOnSubmit);
                setAccessMode(t.accessMode ?? 'InvitedOnly');
                if (t.schemaJson) {
                    try {
                        const schema: TemplateSchema = JSON.parse(t.schemaJson);
                        setFields(schema.fields ?? []);
                        if (schema.languages?.length) setLanguages(schema.languages);
                        if (schema.names)        setNames(schema.names);
                        if (schema.descriptions) setDescriptions(schema.descriptions);
                    } catch { /* invalid json */ }
                }
            })
            .catch(() => toast.error('Failed to load form'))
            .finally(() => setLoading(false));
    }, [token, codeParam]); // eslint-disable-line

    // ── DnD ────────────────────────────────────────────────────────────────────
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setDragActiveType(null);
        if (!over) return;

        const isFromPalette = active.id.toString().startsWith('palette-');

        if (isFromPalette) {
            const type = active.data.current?.type as FieldType;
            const newField = makeField(type, fields.length + 1);

            const overIsCanvas = over.id === 'canvas';
            const overIndex    = overIsCanvas
                ? fields.length
                : fields.findIndex(f => f.id === over.id);

            const updated = [...fields];
            updated.splice(overIndex < 0 ? fields.length : overIndex + 1, 0, newField);
            setFields(updated.map((f, i) => ({ ...f, order: i + 1 })));
            setSelectedFieldId(newField.id);
        } else {
            const oldIdx = fields.findIndex(f => f.id === active.id);
            const newIdx = fields.findIndex(f => f.id === over.id);
            if (oldIdx !== -1 && newIdx !== -1 && oldIdx !== newIdx) {
                setFields(arrayMove(fields, oldIdx, newIdx).map((f, i) => ({ ...f, order: i + 1 })));
            }
        }
    };

    const handleDragStart = (event: any) => {
        if (event.active?.data?.current?.isPaletteItem) {
            setDragActiveType(event.active.data.current.type as FieldType);
        }
    };

    // ── Field mutations ────────────────────────────────────────────────────────
    const updateField = (updated: TemplateField) =>
        setFields(prev => prev.map(f => f.id === updated.id ? updated : f));

    const deleteField = (id: string) => {
        setFields(prev => prev.filter(f => f.id !== id).map((f, i) => ({ ...f, order: i + 1 })));
        if (selectedFieldId === id) setSelectedFieldId(null);
    };

    // ── Save ───────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!name.trim()) { toast.warn('Form name is required'); return; }
        setSaving(true);

        const schemaJson = JSON.stringify({ fields, languages, names, descriptions } satisfies TemplateSchema);
        const primaryName = names['en'] || name;
        const toUtcOrNull = (s: string) => s ? new Date(s).toISOString() : null;

        try {
            if (templateCode) {
                // Update
                const res = await axios.put<TemplateDto>(
                    `${api}/v1/Form/update`,
                    {
                        templateCode,
                        name: primaryName, description: descriptions['en'] || description, status, schemaJson,
                        openDate: toUtcOrNull(openDate),
                        closeDate: toUtcOrNull(closeDate),
                        allowEditResponse: allowEdit,
                        notifyOnPublish: notifyPublish,
                        notifyOnSubmit: notifySubmit,
                        accessMode,
                    },
                    auth()
                );
                setStatus(res.data.status as TemplateStatus);
                toast.success('Form saved');
            } else {
                // Create
                const res = await axios.post<TemplateDto>(
                    `${api}/v1/Form/create`,
                    {
                        name: primaryName, description: descriptions['en'] || description, schemaJson,
                        openDate: toUtcOrNull(openDate),
                        closeDate: toUtcOrNull(closeDate),
                        allowEditResponse: allowEdit,
                        notifyOnPublish: notifyPublish,
                        notifyOnSubmit: notifySubmit,
                        accessMode,
                    },
                    auth()
                );
                setTemplateCode(res.data.templateCode);
                // Update URL to edit mode without full reload
                window.history.replaceState({}, '', `/form-builder?code=${res.data.templateCode}`);
                toast.success(`Form created — ${res.data.templateCode}`);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingScreen />;

    return (
        <div className="admin-layout">
            <Topbar role={role} photoData={photoData ?? undefined} token={token} />
            <main className="admin-main p-0 d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header bar */}
                <div className="template-builder-header">
                    <Button variant="link" className="p-0 text-muted me-2" onClick={() => navigate('/forms')} title="Back">
                        <BiArrowBack size={20} />
                    </Button>
                    <BiLayout size={20} className="text-danger me-1" />
                    <input
                        className="template-builder-name-input"
                        placeholder="Form name…"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                    {templateCode && <span className="text-muted small me-2">{templateCode}</span>}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        title="Download translation template"
                        onClick={() => downloadTranslationExcel(fields, languages, names['en'] || name)}
                        disabled={fields.length === 0}
                    >
                        <BiDownload /> Translations
                    </Button>
                    <label
                        className="btn btn-outline-secondary btn-sm me-2"
                        title="Import translations from Excel"
                        style={{ cursor: 'pointer', marginBottom: 0 }}
                    >
                        <BiUpload /> Import
                        <input
                            type="file"
                            accept=".xlsx"
                            style={{ display: 'none' }}
                            onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                    const updated = await parseTranslationExcel(file, fields);
                                    setFields(updated);
                                    toast.success('Translations imported');
                                } catch {
                                    toast.error('Failed to parse Excel file');
                                }
                                e.target.value = '';
                            }}
                        />
                    </label>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => setShowPreview(true)}
                    >
                        <BiShow /> Preview
                    </Button>
                    <Button
                        size="sm"
                        style={{ background: '#c5281f', border: 'none' }}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? <Spinner size="sm" /> : <><BiSave /> Save</>}
                    </Button>
                </div>

                {/* Tabs */}
                <Nav variant="tabs" className="px-3 border-bottom bg-white" style={{ flexShrink: 0 }}>
                    {(['builder', 'settings', 'respondents', 'reports'] as Tab[]).map(tab => (
                        <Nav.Item key={tab}>
                            <Nav.Link
                                active={activeTab === tab}
                                onClick={() => (tab !== 'reports' || templateCode) && setActiveTab(tab)}
                                className={activeTab === tab ? 'text-danger border-danger' : ''}
                                style={{ cursor: tab === 'reports' && !templateCode ? 'not-allowed' : 'pointer', opacity: tab === 'reports' && !templateCode ? 0.45 : 1 }}
                                title={tab === 'reports' && !templateCode ? 'Save the form first' : undefined}
                            >
                                {tab === 'builder'     ? '① Builder'     :
                                 tab === 'settings'    ? '② Settings'    :
                                 tab === 'respondents' ? '③ Respondents' :
                                 <><BiBarChartAlt2 className="me-1" />④ Reports</>}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                    {templateCode && (
                        <span className="ms-auto d-flex align-items-center">
                            <span className={`template-status-badge template-status-badge--${status.toLowerCase()}`}>
                                {status}
                            </span>
                        </span>
                    )}
                </Nav>

                {/* Tab content */}
                {activeTab === 'builder' && (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="template-builder-workspace" style={{ flex: 1, overflow: 'hidden' }}>
                            <FieldPalette />

                            <BuilderCanvas
                                fields={fields}
                                selectedFieldId={selectedFieldId}
                                onSelect={id => setSelectedFieldId(id || null)}
                                onDelete={deleteField}
                            />

                            {selectedField && (
                                <FieldEditor
                                    field={selectedField}
                                    onChange={updateField}
                                    onClose={() => setSelectedFieldId(null)}
                                    languages={languages}
                                />
                            )}
                        </div>

                        <DragOverlay>
                            {dragActiveType && (
                                <div className="field-palette__item field-palette__item--active">
                                    {dragActiveType.replace(/_/g, ' ')}
                                </div>
                            )}
                        </DragOverlay>
                    </DndContext>
                )}

                {activeTab === 'settings' && (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <FormSettings
                            name={name}
                            description={description}
                            status={status}
                            openDate={openDate}
                            closeDate={closeDate}
                            allowEditResponse={allowEdit}
                            notifyOnPublish={notifyPublish}
                            notifyOnSubmit={notifySubmit}
                            accessMode={accessMode}
                            languages={languages}
                            names={names}
                            descriptions={descriptions}
                            onLanguagesChange={setLanguages}
                            onNamesChange={setNames}
                            onDescriptionsChange={setDescriptions}
                            onChange={patch => {
                                if (patch.name              !== undefined) setName(patch.name!);
                                if (patch.description       !== undefined) setDescription(patch.description!);
                                if (patch.status            !== undefined) setStatus(patch.status!);
                                if (patch.openDate          !== undefined) setOpenDate(patch.openDate!);
                                if (patch.closeDate         !== undefined) setCloseDate(patch.closeDate!);
                                if (patch.allowEditResponse !== undefined) setAllowEdit(patch.allowEditResponse!);
                                if (patch.notifyOnPublish   !== undefined) setNotifyPublish(patch.notifyOnPublish!);
                                if (patch.notifyOnSubmit    !== undefined) setNotifySubmit(patch.notifyOnSubmit!);
                                if (patch.accessMode        !== undefined) setAccessMode(patch.accessMode!);
                            }}
                        />
                    </div>
                )}

                {activeTab === 'respondents' && (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {!templateCode ? (
                            <div className="text-center text-muted py-5">
                                Save the form first before adding respondents.
                            </div>
                        ) : (
                            <FormRespondents templateCode={templateCode} token={token} />
                        )}
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <FormReportPanel
                            token={token}
                            templateCode={templateCode}
                            formName={name || templateCode}
                            fields={fields}
                        />
                    </div>
                )}

                {showPreview && (
                    <FormPreviewModal
                        show
                        onHide={() => setShowPreview(false)}
                        name={name || 'Preview'}
                        description={description || undefined}
                        fields={fields}
                        languages={languages}
                        names={names}
                        descriptions={descriptions}
                    />
                )}
            </main>
        </div>
    );
}
